/**
 * `Display` union — structured-data results carried on chat `answer` frames.
 *
 * Wire shape (BE source of truth): each entry in `WSChatEvent.displays[]`
 * is wrapped by ``enterprise_pipeline._build_display()`` in
 * ``ando-agent/src/services/agent_routing/enterprise_pipeline.py`` (the
 * `structured_data_result` branch around line 351). The inner ``QueryResult``
 * comes from ``ando-agent/src/services/enterprise_connectors/text_to_sql.py``
 * (`@dataclass QueryResult`, line 209).
 *
 * Variants are discriminated by ``source_type``:
 *   - ``"sql"``       → relational connector (Salesforce, FiC, Postgres, …)
 *   - ``"autocad"``   → DXF fact_metrics (CAD Intelligence)
 *   - ``"banking"``   → Open Banking bank_transactions
 *
 * Forward-compat strategy
 * -----------------------
 * The BE may introduce new connector kinds (PEC, SDI, payroll…) before the
 * FE ships dedicated renderers. To avoid a hard parse failure the moment
 * a new ``source_type`` lands, the union is built with ``z.union`` (NOT
 * ``z.discriminatedUnion``) plus an explicit ``UnknownDisplaySchema``
 * fall-through that:
 *   1. preserves the raw payload (``passthrough``) so debugging UIs can
 *      still inspect the dict,
 *   2. fires a ``console.warn`` with the unknown ``source_type`` so the
 *      next deploy notices the new variant without crashing the chat
 *      surface.
 *
 * Why not ``discriminatedUnion``?
 *   - Zod's discriminated union rejects values whose discriminator isn't
 *     in the literal set — exactly the failure mode we want to avoid.
 *
 * The shared base (``BaseDisplayFieldsSchema``) carries the fields every
 * branch needs: ``data``, ``sql``, ``confidence``, etc. ``columns`` is
 * accepted as an optional pre-computed list (the BE may emit it in the
 * future) but defaults to "derive client-side from ``Object.keys(data[0])``".
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Envelope type — the BE always wraps a structured-data display in
// ``type: "structured_data_result"``. ``columns`` is FE-future-proofing.
// ---------------------------------------------------------------------------

/**
 * BE-canonical chart spec shape. Same opacity policy as
 * ``ChartVisualization`` in ``api/endpoints/dashboard.ts``: ECharts-shaped
 * fields stay loose at the wire boundary, narrowed only when the chart
 * renderer (PR-A2) consumes them.
 */
export const DisplayChartSpecSchema = z.record(z.string(), z.unknown());
export type DisplayChartSpec = z.infer<typeof DisplayChartSpecSchema>;

/** Single row of result data. Keys = column names, values = anything. */
export const DisplayRowSchema = z.record(z.string(), z.unknown());
export type DisplayRow = z.infer<typeof DisplayRowSchema>;

/**
 * Server-authored capability for changing the period of a certified
 * structured-data result. The discriminated union keeps the compact year
 * selector and absolute date/date-time ranges on the same wire contract.
 *
 * The token is deliberately opaque: the client never rewrites SQL or chooses
 * a physical time column. It sends the token back with the selected year and
 * the server resolves the original semantic binding, re-runs the governed
 * query pipeline and returns a complete replacement display.
 */
const TemporalYearControlSchema = z.object({
  version: z.literal(1),
  token: z.string().min(1),
  selector: z.literal("year"),
  value: z.number().int(),
  min: z.number().int().optional(),
  max: z.number().int().optional(),
});

const TemporalRangeBoundarySchema = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/,
    "Expected an ISO date or date-time",
  );

const TemporalRangeValueSchema = z.object({
  start: TemporalRangeBoundarySchema,
  end: TemporalRangeBoundarySchema,
});

export const TemporalPresetUnitSchema = z.enum([
  "minute",
  "hour",
  "day",
  "week",
  "month",
  "quarter",
  "year",
]);
export type TemporalPresetUnit = z.infer<typeof TemporalPresetUnitSchema>;

const TemporalRangeControlSchema = z.object({
  version: z.literal(1),
  token: z.string().min(1),
  selector: z.literal("range"),
  value: TemporalRangeValueSchema,
  precision: z.enum(["date", "datetime"]),
  /** Time-zone/offset context preserved by the certified SQL boundaries. */
  timezone: z.string().min(1).nullable(),
  supported_presets: z.array(TemporalPresetUnitSchema),
  min: TemporalRangeBoundarySchema.optional(),
  max: TemporalRangeBoundarySchema.optional(),
});

export const TemporalControlSchema = z
  .discriminatedUnion("selector", [TemporalYearControlSchema, TemporalRangeControlSchema])
  .superRefine((control, ctx) => {
    const asOrder = (value: number | string): number =>
      typeof value === "number" || control.selector === "year" || control.precision === "date"
        ? Number(value.toString().replaceAll("-", "").slice(0, 8))
        : Date.parse(value);
    const lowerBound = control.min === undefined ? undefined : asOrder(control.min);
    const upperBound = control.max === undefined ? undefined : asOrder(control.max);
    const start = asOrder(control.selector === "year" ? control.value : control.value.start);
    const end = asOrder(control.selector === "year" ? control.value : control.value.end);

    if (lowerBound !== undefined && upperBound !== undefined && lowerBound > upperBound) {
      ctx.addIssue({
        code: "custom",
        message: "temporal_control.min must be less than or equal to max",
        path: ["min"],
      });
    }
    if (control.selector === "range" && start >= end) {
      ctx.addIssue({
        code: "custom",
        message: "temporal_control start must be less than end",
        path: ["value", "start"],
      });
    }
    if (lowerBound !== undefined && start < lowerBound) {
      ctx.addIssue({
        code: "custom",
        message: "temporal_control.value must be greater than or equal to min",
        path: control.selector === "year" ? ["value"] : ["value", "start"],
      });
    }
    if (upperBound !== undefined && end > upperBound) {
      ctx.addIssue({
        code: "custom",
        message: "temporal_control.value must be less than or equal to max",
        path: control.selector === "year" ? ["value"] : ["value", "end"],
      });
    }
    if (
      control.selector === "range" &&
      control.precision === "date" &&
      [control.value.start, control.value.end, control.min, control.max]
        .filter((value): value is string => value !== undefined)
        .some((value) => value.includes("T"))
    ) {
      ctx.addIssue({
        code: "custom",
        message: "date precision does not accept time components",
        path: ["value"],
      });
    }
  });
export type TemporalControl = z.infer<typeof TemporalControlSchema>;

/**
 * Fields common to every ``structured_data_result`` variant. Kept as a
 * standalone object so each branch (`sql` / `autocad` / `banking`) can
 * ``.extend`` it with its own additions without duplicating.
 */
const BaseDisplayFieldsSchema = z.object({
  /** Wire envelope discriminator — always ``"structured_data_result"``. */
  type: z.literal("structured_data_result"),
  /** Inner connector kind. The actual variant discriminator. */
  source_type: z.string(),
  /** Result rows — empty array when the query returned no rows. */
  data: z.array(DisplayRowSchema).default([]),
  /**
   * Pre-computed column order. Optional: the BE does NOT emit this today
   * (FE derives it from ``Object.keys(data[0])`` at render time), but
   * keeping the slot lets future BE deploys preempt the inference.
   */
  columns: z.array(z.string()).optional(),
  /** SQL the BE actually executed (after agent-loop + validation). */
  sql: z.string().nullable().optional(),
  /** Total rows returned by the connector. */
  row_count: z.number().nonnegative().default(0),
  /** Time the BE spent executing + post-processing (excludes WS RTT). */
  execution_time_ms: z.number().nonnegative().default(0),
  /** [0,1] — BE's self-assessed confidence (label colour in the UI). */
  confidence: z.number().min(0).max(1).default(0),
  /** Agent-loop retry count (Pydantic AI tool-call iterations). */
  retry_count: z.number().nonnegative().default(0),
  /** LLM tokens consumed across the agent loop. */
  tokens_used: z.number().nonnegative().default(0),
  /** Originating connection id — owns the SQL dialect + credentials. */
  connection_id: z.string().optional(),
  /** SQL dialect (``postgresql`` / ``salesforce`` / ``fic`` / …). */
  dialect: z.string().nullable().optional(),
  /** Optional ECharts spec when the BE pre-built a chart. ``nullish`` (not
   *  ``optional``): the BE emits an explicit ``null`` when a display has no
   *  chart — accepting it here so a single null field never fails the whole
   *  display parse and blanks the chip + dashboard (Jul 9, 2026). */
  chart_spec: DisplayChartSpecSchema.nullish(),
  /**
   * Legacy alias for ``chart_spec`` — the BE currently emits
   * ``chart_config``. Both are accepted so the FE doesn't have to ship
   * a rename in lock-step with the BE. ``nullish`` for the same null-safety
   * reason as ``chart_spec`` above.
   */
  chart_config: DisplayChartSpecSchema.nullish(),
  /** Error message when the BE surfaced a partial result. */
  error: z.string().nullable().optional(),
  /**
   * Optional semantic re-query capability. It lives on the shared wire base
   * because relational connectors usually emit their provider name
   * (postgresql, salesforce, spreadsheet, ...) rather than the literal
   * ``source_type: "sql"``.
   */
  temporal_control: TemporalControlSchema.nullish(),
  /**
   * Provenance-engine marker (Jul 9, 2026). ``"fusion"`` / ``"structured"``
   * flags a display backed by REAL structured rows from the TERRA fusion path
   * — the PRIMARY answer of a cross turn, not a side-effect. The dashboard
   * auto-open promotes it even in ``focus="standard"`` (the user invoked TERRA
   * without ticking a connector chip; that IS the structured intent — same
   * behaviour a connector turn gets for free). Absent on enterprise-connector
   * displays (those already run in structured/full focus). Optional so older
   * BE deploys and the specialised (autocad/banking) variants parse unchanged.
   */
  source: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Branch schemas — discriminated by ``source_type``
// ---------------------------------------------------------------------------

export const SqlDisplaySchema = BaseDisplayFieldsSchema.extend({
  source_type: z.literal("sql"),
  /** Presentation-only axis completion; raw data and row_count remain evidence. */
  presentation: z.object({
    kind: z.literal("monthly_axis"),
    dimension: z.string(),
    measure_columns: z.array(z.string()).optional(),
    year: z.number().int(),
    rows: z.array(DisplayRowSchema),
    missing_buckets: z.array(z.number().int().min(1).max(12)),
  }).nullish(),
});
export type SqlDisplay = z.infer<typeof SqlDisplaySchema>;

/**
 * CAD Intelligence — DXF fact_metrics result. Adds ``source_files`` so the
 * file-selector chip can filter to the underlying drawings the agent hit.
 */
/** Per-file viewer metadata emitted by the BE alongside ``source_files``.
 *  Pre-resolved MinIO coordinates let the FE compose the proxy URL for
 *  the WebGL viewer without an extra round-trip. Optional for back-compat. */
export const CadSourceFileInfoSchema = z.object({
  filename: z.string(),
  content_item_id: z.string().nullable().optional(),
  // Source bytes (original ``.stp`` / ``.igs`` / ``.dxf`` / ``.stl``).
  // Used for "Download original" + the DXF 2D viewer (which parses
  // source bytes natively).
  minio_object_key: z.string().nullable().optional(),
  minio_bucket: z.string().nullable().optional(),
  // Viewer payload — points at the tessellated STL artefact for
  // STEP/IGES, or the source bytes for STL/DXF (where source IS the
  // mesh / 2D viewer can parse source). Optional for back-compat:
  // older BE responses didn't emit these and the FE falls back to
  // ``minio_object_key`` — that's fine for DXF/STL but produces a
  // parse error for STEP/IGES (Jun 17, 2026 fix).
  viewer_minio_object_key: z.string().nullable().optional(),
  viewer_minio_bucket: z.string().nullable().optional(),
  cad_format: z.string().nullable().optional(),
});
export type CadSourceFileInfo = z.infer<typeof CadSourceFileInfoSchema>;

/** CAD rectangle the user drew on the drawing (BE echoes it back so the
 *  viewer can overlay the region. Optional — only present when the query
 *  was triggered via "Analizza CAD"). */
export const CadSpatialFilterSchema = z.object({
  source_file: z.string(),
  min_x: z.number(),
  max_x: z.number(),
  min_y: z.number(),
  max_y: z.number(),
});
export type CadSpatialFilter = z.infer<typeof CadSpatialFilterSchema>;

export const CadDisplaySchema = BaseDisplayFieldsSchema.extend({
  source_type: z.literal("autocad"),
  source_files: z.array(z.string()).optional(),
  /** Pre-resolved viewer info — one per ``source_files`` entry. */
  source_files_info: z.array(CadSourceFileInfoSchema).optional(),
  /** Rectangle the user drew on the drawing (Analizza CAD modal). */
  cad_spatial_filter: CadSpatialFilterSchema.optional(),
});
export type CadDisplay = z.infer<typeof CadDisplaySchema>;

/**
 * Open Banking — ``bank_transactions`` result. The BE may surface bespoke
 * KPI / category metadata in future; left open for now.
 */
export const BankDisplaySchema = BaseDisplayFieldsSchema.extend({
  source_type: z.literal("banking"),
});
export type BankDisplay = z.infer<typeof BankDisplaySchema>;

/**
 * Catch-all for future BE variants. Uses ``passthrough()`` so the raw
 * payload survives the parse for debugging surfaces; emits a warning the
 * first time each unknown variant is seen so the next deploy notices.
 */
const UnknownDisplaySchema = BaseDisplayFieldsSchema.loose().transform(
  (value) => {
    warnUnknownSourceType(value.source_type);
    return value;
  },
);
export type UnknownDisplay = z.infer<typeof UnknownDisplaySchema>;

const KNOWN_SOURCE_TYPES = new Set<string>(["sql", "autocad", "banking"]);
const warnedSourceTypes = new Set<string>();

function warnUnknownSourceType(sourceType: string): void {
  if (KNOWN_SOURCE_TYPES.has(sourceType)) return;
  if (warnedSourceTypes.has(sourceType)) return;
  warnedSourceTypes.add(sourceType);
  // Boundary log — fires once per unknown source_type per page load so
  // CI / staging picks up unrecognised BE variants. Production hits land
  // in the browser console for the on-call to triage.
  console.warn(
    `[displays] Unknown source_type "${sourceType}" — falling back to passthrough. Add a dedicated variant or update the FE.`,
  );
}

/**
 * Reset hook for tests — the warn-once cache leaks across `it` blocks
 * otherwise, hiding regressions in suites that rely on the warning
 * firing per fresh parse. Not exported through the barrel intentionally;
 * test files import it directly.
 */
export function __resetUnknownSourceTypeCacheForTests(): void {
  warnedSourceTypes.clear();
}

// ---------------------------------------------------------------------------
// Union — warn-and-keep
// ---------------------------------------------------------------------------

/**
 * Top-level ``Display`` schema. Tries each known branch in order; falls
 * through to ``UnknownDisplaySchema`` (which always succeeds) when none
 * match. Returning a successful parse for unknown variants is the
 * documented forward-compat strategy — feature renderers downstream are
 * expected to dispatch on ``source_type`` and skip what they don't know.
 */
export const DisplaySchema = z.union([
  SqlDisplaySchema,
  CadDisplaySchema,
  BankDisplaySchema,
  UnknownDisplaySchema,
]);
export type Display = z.infer<typeof DisplaySchema>;

/** Convenience array schema — used at the WS / rebuild boundary. */
export const DisplayArraySchema = z.array(DisplaySchema);
export type DisplayArray = z.infer<typeof DisplayArraySchema>;

/**
 * Safe-parse helper: returns the parsed array on success, ``undefined``
 * when the input is falsy / not an array. Individual rows that fail the
 * branch schemas are dropped (a single malformed display shouldn't blank
 * the whole answer turn).
 */
export function parseDisplaysLoose(value: unknown): Display[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: Display[] = [];
  for (const raw of value) {
    const parsed = DisplaySchema.safeParse(raw);
    if (parsed.success) out.push(parsed.data);
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Extract + parse the ``displays`` slot from a WS / rebuild frame.
 *
 * The BE has historically wavered between two locations for this field:
 *   1. **top-level** — ``frame.displays`` (current ``answer``/``result``
 *      envelope, also used by the rebuild rows)
 *   2. **nested in payload** — ``frame.payload.displays`` (legacy / some
 *      proxy paths)
 *
 * Both are accepted so the FE doesn't have to coordinate a flag-day
 * rename with the BE. Use this helper at every boundary that wants
 * ``MessageView.displays`` — wsHandlers + useConversationDetail — so the
 * resolution rule stays in one place. (May 31, 2026 — was duplicated
 * inline at both call sites, and the wsHandlers copy dropped the
 * ``payload.displays`` fallback, producing the "Risultato non
 * disponibile" panel state even when ``wirePayload.displays`` clearly
 * carried the result.)
 */
export function pickDisplays(
  frame: { displays?: unknown; payload?: unknown } | null | undefined,
): Display[] | undefined {
  if (!frame) return undefined;
  const nested =
    typeof frame.payload === "object" && frame.payload !== null
      ? (frame.payload as { displays?: unknown }).displays
      : undefined;
  return parseDisplaysLoose(frame.displays ?? nested);
}

// ---------------------------------------------------------------------------
// Media displays (generated image) — a SEPARATE family from
// ``structured_data_result``. The agent emits ``{type:"generated_image", …}``
// which does NOT fit ``BaseDisplayFields`` (no ``source_type``) and is dropped
// by ``pickDisplays`` on purpose — it isn't a dashboard/CAD/banking result.
// We surface it via its own tiny schema + extractor so the chat can show the
// image inline + auto-open it, without polluting the structured ``Display``
// union (and the routing / ``displayKind`` that depends on it). June 2026.
// ---------------------------------------------------------------------------

export const GeneratedImageDisplaySchema = z.object({
  type: z.literal("generated_image"),
  /** API-relative URL to the rendered image (``/api/v1/minio-images/…``). */
  image_url: z.string(),
  /** content_items weaviate_doc_id (``generated_<uuid>``) — auto-open key.
   *  Optional for back-compat with payloads emitted before doc_id was added;
   *  the FE then derives it from ``image_url``. */
  doc_id: z.string().optional(),
  prompt: z.string().optional(),
  aspect_ratio: z.string().optional(),
  mime_type: z.string().optional(),
});
export type GeneratedImageDisplay = z.infer<typeof GeneratedImageDisplaySchema>;

/**
 * Same shape as ``GeneratedImageDisplaySchema`` but for SVG payloads emitted
 * by the BE ``generate_svg`` Pydantic AI tool. The chat surface treats SVG
 * like a generated image (chip → ArtifactPane), so the schema is intentionally
 * a mirror — the BE writes the asset as ``image/svg+xml`` in MinIO and the
 * existing ``isImageMime()`` predicate in ``ArtifactPane`` renders it via
 * the native ``<img>`` path. June 14, 2026.
 *
 * ``svg_type`` is the BE-side semantic label (``diagram`` / ``chart`` /
 * ``icon`` / ``infographic`` / ``illustration`` / ``schema``) — kept as a
 * loose string so a new BE category lights up without an FE redeploy.
 */
export const GeneratedSvgDisplaySchema = z.object({
  type: z.literal("generated_svg"),
  svg_url: z.string(),
  doc_id: z.string().optional(),
  prompt: z.string().optional(),
  svg_type: z.string().optional(),
  mime_type: z.string().optional(),
});
export type GeneratedSvgDisplay = z.infer<typeof GeneratedSvgDisplaySchema>;

/**
 * Inline chart payload emitted by the agent in RAG-classic mode (when the
 * answer text references a synthesised visualisation). The BE shapes match
 * the V1 contract — ``{type: "<chart_kind>", objects: [<one-or-more
 * chart objects>]}`` — and are validated server-side via
 * ``pydantic_agent.DisplayPayload`` (discriminated union over the five
 * chart kinds + table).
 *
 * Why this isn't part of the structured-data ``DisplaySchema`` union: the
 * structured-data union is discriminated on ``source_type`` (sql / autocad
 * / banking) — these chart payloads have no ``source_type`` because they
 * aren't a connector result. Surfacing them via their own extractor mirrors
 * the ``generated_image`` / ``generated_svg`` pattern (June 14, 2026 —
 * see ``feedback_v1_display_parity_audit_jun14``).
 *
 * The schema is intentionally loose on ``objects`` (``record`` rather
 * than a per-kind nested schema) so a future BE-side payload tweak (extra
 * ``description`` field, normalised axis flag, …) lights up FE without
 * a coordinated redeploy. The renderer (``inlineChartToOption``) tolerates
 * missing fields with sensible defaults.
 */
export const InlineChartDisplayKindSchema = z.enum([
  "bar_chart",
  "scatter_or_line_chart",
  "histogram_chart",
  "aggregation",
  "table",
]);
export type InlineChartDisplayKind = z.infer<typeof InlineChartDisplayKindSchema>;

export const InlineChartDisplaySchema = z.object({
  type: InlineChartDisplayKindSchema,
  objects: z.array(z.record(z.string(), z.unknown())).default([]),
});
export type InlineChartDisplay = z.infer<typeof InlineChartDisplaySchema>;

/**
 * Voice of X report preview embed (Workstream B — M2.4, June 17, 2026).
 *
 * Emitted by the agent when a Voice-of-X report has just been generated /
 * surfaced as the answer to a turn. Mirrors the ``generated_image`` /
 * ``generated_svg`` pattern: a thin descriptor the FE renders as a chat
 * preview card and uses to deep-link into the workspace report viewer.
 *
 * Why a dedicated kind (not just an inline-chart fallback): a report is a
 * MULTI-section structured artifact (taxonomy + citations + verbatims) —
 * collapsing it to a chart chip would hide what the user is looking at.
 * The chip exposes the three highest-signal facts (title, scoring,
 * category count, exec summary) so the user knows whether the report is
 * what they need before navigating.
 *
 * ``final_score`` mirrors ``ReportNative.evaluation_scores.deterministic.confidence``
 * (the headline quality signal — already a ``NormalizedScoreSchema``).
 * ``n_categories`` mirrors ``ReportNative.taxonomy.categories.length``.
 */
export const VoiceReportEmbedDisplaySchema = z.object({
  type: z.literal("voice_report"),
  /** ``reports.id`` (UUID) — the deep-link key for the report viewer route. */
  report_id: z.string(),
  /** Report headline (mirrors ``ReportDocBase.surveyInfo.title`` when present). */
  title: z.string(),
  /** Discovered category count (``taxonomy.categories.length``). */
  n_categories: z.number().int().nonnegative(),
  /** Headline quality score in [0, 1] (``evaluation_scores.deterministic.confidence``). */
  final_score: z.number().min(0).max(1),
  /** Two-to-three sentence executive summary surfaced verbatim on the chip. */
  executive_summary: z.string(),
});
export type VoiceReportEmbedDisplay = z.infer<typeof VoiceReportEmbedDisplaySchema>;

/**
 * Clarification request (Aug 2026) — the BE's answer when a data question is
 * AMBIGUOUS and guessing would produce a confidently WRONG number.
 *
 * Wire shape (BE source of truth):
 *   - built by ``enterprise_pipeline._run_enterprise_connector`` (the
 *     ``elif result.clarifying_questions:`` branch) as
 *     ``{type: "clarification_needed", questions: [...], error, connection_id,
 *     dialect, tokens_used}``;
 *   - the entries come from ``text_to_sql._generate_clarifying_questions``
 *     (rule-based, no LLM) and from
 *     ``spreadsheet_agg_templates.AggAmbiguity.to_clarifying_questions``
 *     (the canonical "«importo» → «Importo Lordo» | «Importo Netto»" case).
 *
 * Why its OWN schema instead of a ``Display`` union branch: it carries no
 * ``source_type`` and no ``data`` — it isn't a connector RESULT, it's the
 * connector declining to answer. ``pickDisplays`` drops it on purpose (same
 * treatment as ``generated_image`` / ``inline_chart``); this extractor
 * surfaces it as its own ``MessageView`` field.
 *
 * Lifecycle note: this is INLINE in the answer turn — NOT a pending action.
 * Clicking an option simply sends ``suggested_query`` as a new user message
 * (no POST, no Temporal fork to unblock — that's
 * ``features/source-confirmation``'s job, a different contract).
 */
export const ClarificationContextSchema = z.looseObject({
  /** The ambiguous word as it appeared in the user's question ("importo"). */
  term: z.string().optional(),
  /** The exact catalog name this option resolves the term to ("Importo Lordo"). */
  chosen: z.string().optional(),
  /** Every exact catalog name in play — the full choice set. */
  candidates: z.array(z.string()).optional(),
});
export type ClarificationContext = z.infer<typeof ClarificationContextSchema>;

export const ClarificationQuestionSchema = z.object({
  /** Full sentence the BE composed ("Con «importo» intendi «Importo Lordo»?"). */
  question: z.string(),
  /**
   * BE taxonomy: ``entity`` / ``column`` / ``value`` / ``date_range`` /
   * ``aggregation`` / ``filter`` / ``complexity`` / ``general`` — plus
   * ones the BE adds without an FE redeploy (``join`` landed Aug 10, 2026).
   * Kept a bare ``string`` on purpose: the renderer maps KNOWN values to an
   * icon + a translated label and falls back gracefully for the rest.
   */
  category: z.string().default("general"),
  /**
   * The disambiguated question to re-send verbatim on click. Nullable: a
   * few BE signals produce a question with no actionable rewrite, in which
   * case the row renders as plain prose instead of a button.
   */
  suggested_query: z.string().nullable().optional(),
  context: ClarificationContextSchema.nullish(),
});
export type ClarificationQuestion = z.infer<typeof ClarificationQuestionSchema>;

export const ClarificationNeededDisplaySchema = z.object({
  type: z.literal("clarification_needed"),
  /**
   * Per-entry warn-and-drop (mirrors ``parseDisplaysLoose``): one malformed
   * question must never blank the whole card — the user would then see the
   * agent's hedging prose with NO way to act on it, which is strictly worse
   * than showing the two options that did parse.
   */
  questions: z
    .array(z.unknown())
    .default([])
    .transform((raw) =>
      raw.flatMap((entry) => {
        const parsed = ClarificationQuestionSchema.safeParse(entry);
        return parsed.success ? [parsed.data] : [];
      }),
    ),
  /** Raw connector error — surfaced behind a quiet disclosure, never up front. */
  error: z.string().nullable().optional(),
  connection_id: z.string().nullable().optional(),
  dialect: z.string().nullable().optional(),
  tokens_used: z.number().nonnegative().optional(),
});
export type ClarificationNeededDisplay = z.infer<typeof ClarificationNeededDisplaySchema>;

/** A business metric needs the user's own definition before a figure is certified. */
export const BusinessDefinitionNeededDisplaySchema = z.object({
  type: z.literal("business_definition_needed"),
  question: z.string().min(1),
  measured_value_label: z.string().optional(),
  definition_token: z.string().min(1),
  original_query: z.string().min(1),
  connection_id: z.string().nullish(),
});
export type BusinessDefinitionNeededDisplay = z.infer<typeof BusinessDefinitionNeededDisplaySchema>;

export function extractBusinessDefinitionNeeded(
  frame: { displays?: unknown; payload?: unknown } | null | undefined,
): BusinessDefinitionNeededDisplay | null {
  return _extractFirstMedia(frame, BusinessDefinitionNeededDisplaySchema);
}

/**
 * Extract the FIRST ``clarification_needed`` payload from a WS / rebuild
 * frame. Same ``displays`` / ``payload.displays`` walk as the media
 * extractors (``_extractFirstMedia``) so a BE-side envelope tweak lights up
 * every kind at once.
 */
export function extractClarificationNeeded(
  frame: { displays?: unknown; payload?: unknown } | null | undefined,
): ClarificationNeededDisplay | null {
  return _extractFirstMedia(frame, ClarificationNeededDisplaySchema);
}

/**
 * Extract the FIRST ``generated_image`` media display from a WS/rebuild frame
 * (top-level ``displays`` or ``payload.displays``), or ``null``. Never throws.
 * Kept separate from ``pickDisplays`` so the structured-data pipeline is
 * untouched.
 */
export function extractGeneratedImage(
  frame: { displays?: unknown; payload?: unknown } | null | undefined,
): GeneratedImageDisplay | null {
  return _extractFirstMedia(frame, GeneratedImageDisplaySchema);
}

/**
 * Mirror of ``extractGeneratedImage`` for SVG payloads. Both extractors
 * share the same array walk + safeParse loop (see ``_extractFirstMedia``)
 * so adding a future ``generated_audio`` / ``generated_video`` is a
 * one-line schema swap. June 14, 2026.
 */
export function extractGeneratedSvg(
  frame: { displays?: unknown; payload?: unknown } | null | undefined,
): GeneratedSvgDisplay | null {
  return _extractFirstMedia(frame, GeneratedSvgDisplaySchema);
}

/**
 * Inline chart extractor — walks the same ``displays`` / ``payload.displays``
 * slot ``extractGeneratedImage`` does. Returns the FIRST chart-kind payload
 * (``bar_chart`` / ``scatter_or_line_chart`` / ``histogram_chart`` /
 * ``aggregation`` / ``table``) so the chat surface can mount a single
 * "Grafico e dati" chip even when the BE emits multiple chart variants for
 * the same turn (the FE renderer multiplexes them internally).
 */
export function extractInlineChart(
  frame: { displays?: unknown; payload?: unknown } | null | undefined,
): InlineChartDisplay | null {
  return _extractFirstMedia(frame, InlineChartDisplaySchema);
}

/**
 * Voice-of-X report extractor — mirror of ``extractGeneratedImage`` /
 * ``extractGeneratedSvg`` (Workstream B M2.4). Walks the same
 * ``displays`` / ``payload.displays`` slot so a future BE-side payload
 * tweak lights up FE without a coordinated redeploy.
 */
export function extractVoiceReportEmbed(
  frame: { displays?: unknown; payload?: unknown } | null | undefined,
): VoiceReportEmbedDisplay | null {
  return _extractFirstMedia(frame, VoiceReportEmbedDisplaySchema);
}

function _extractFirstMedia<T extends z.ZodTypeAny>(
  frame: { displays?: unknown; payload?: unknown } | null | undefined,
  schema: T,
): z.infer<T> | null {
  if (!frame) return null;
  const nested =
    typeof frame.payload === "object" && frame.payload !== null
      ? (frame.payload as { displays?: unknown }).displays
      : undefined;
  const raw = frame.displays ?? nested;
  if (!Array.isArray(raw)) return null;
  for (const entry of raw) {
    const parsed = schema.safeParse(entry);
    if (parsed.success) return parsed.data;
  }
  return null;
}

/** Narrow a `Display` to a specific `source_type`. */
export function isDisplayOfType<T extends Display["source_type"]>(
  display: Display,
  sourceType: T,
): display is Extract<Display, { source_type: T }> {
  return display.source_type === sourceType;
}

/**
 * Rendering category for a ``structured_data_result`` display.
 *
 * NOT a 1:1 mirror of ``source_type``: the BE emits the underlying
 * connector / provider verbatim (``postgresql`` / ``mysql`` /
 * ``salesforce`` / ``fatture_in_cloud`` / ``spreadsheet`` /
 * ``internal_factdim`` / …) — that lives in ``source_type``. The FE
 * doesn't render a different shape for each: there are exactly TWO
 * specialised renderers (``autocad`` for DXF fact_metrics, ``banking``
 * for transaction streams) and a generic SQL/table/chart surface that
 * fits everything else (May 31, 2026 — mirrors the V1 dispatcher in
 * ``ando-frontend/app/components/chat/RenderChat.tsx``).
 *
 * Forward-compat: a new BE connector (Snowflake, Redshift, BigQuery,
 * Stripe-data, …) lands as ``"sql"`` automatically because the rule is
 * "anything that isn't autocad / banking" — no FE redeploy needed.
 */
export type DisplayKind = "sql" | "autocad" | "banking";

const SPECIALISED_KINDS = new Set<string>(["autocad", "banking"]);

/**
 * Resolve a display to its rendering category. Source of truth for both
 * the inline card chooser (chat row) and the panel/chip surfaces.
 */
export function displayKind(display: Display): DisplayKind {
  if (display.source_type === "autocad") return "autocad";
  if (display.source_type === "banking") return "banking";
  return "sql";
}

/**
 * Predicate equivalent of {@link displayKind} — convenient for
 * ``Array.find`` / ``Array.filter`` at the consumer side.
 *
 * Why a predicate AND a getter: the predicate keeps the V1 dispatcher
 * ergonomics (``displays.find(d => matches(d, "sql"))``) while
 * ``displayKind`` lets surfaces that pivot on the category (e.g. the
 * inline card chooser) use a single switch without re-implementing the
 * exclusion rule.
 */
export function displayMatchesKind(
  display: Display,
  kind: DisplayKind,
): boolean {
  if (kind === "sql") return !SPECIALISED_KINDS.has(display.source_type);
  return display.source_type === kind;
}
